import {computed, ref, watch} from 'vue'
import axios from 'axios'
import {useCompany} from './useCompany'

const storedPermissions = sessionStorage.getItem('user_permissions')
const storedCompanyId = sessionStorage.getItem('current_company_id')

const permissions = ref(storedPermissions ? JSON.parse(storedPermissions) : [])
const companyId = ref(storedCompanyId || null)
const loadingPromise = ref(null)
const isLoaded = ref(!!storedPermissions && !!storedCompanyId)

watch(permissions, (newPermissions) => {
    sessionStorage.setItem('user_permissions', JSON.stringify(newPermissions))
}, { deep: true })

watch(companyId, (newCompanyId) => {
    if (newCompanyId) {
        sessionStorage.setItem('current_company_id', newCompanyId)
    }
})

export function usePermissions(initialCompanyId = null) {
    const { getCompanyId, buildCompanyApiInternalRoute } = useCompany()

    const localCompanyId = ref(initialCompanyId)

    if (localCompanyId.value) {
        loadCompany(localCompanyId.value)
    }

    const can = (permission) => {
        if (!permissions.value || !Array.isArray(permissions.value)) {
            return false
        }

        return permissions.value.indexOf(permission) !== -1
    }

    const getPermissions = () => {
        if (!permissions.value || !Array.isArray(permissions.value)) {
            return []
        }

        return permissions.value
    }

    const canAsync = async (permission, targetCompanyId = null) => {
        if (!targetCompanyId) {
            targetCompanyId = getCompanyId('sonicu')
        }

        await loadCompany(targetCompanyId)

        return can(permission)
    }

    const isCompanyLoaded = (targetCompanyId) => {
        return isLoaded.value && companyId.value === targetCompanyId
    }

    const loadCompany = (targetCompanyId = null) => {
        if (targetCompanyId === null) {
            targetCompanyId = getCompanyId('sonicu')
        }

        // If switching companies, clear old permissions
        if (companyId.value && companyId.value !== targetCompanyId) {
            permissions.value = []
            isLoaded.value = false
            loadingPromise.value = null
        }

        companyId.value = targetCompanyId

        // If permissions already loaded for this company, return immediately
        if (isLoaded.value && companyId.value === targetCompanyId) {
            return Promise.resolve(permissions.value)
        }

        // If already loading, return the existing promise
        if (loadingPromise.value) {
            return loadingPromise.value
        }

        // Create new loading promise
        const promise = new Promise((resolve, reject) => {
            const url = buildCompanyApiInternalRoute('permissions', targetCompanyId)
            fetch(url)
                .then(response => response.json())
                .then(data => {
                    permissions.value = data.permissions || data
                    isLoaded.value = true
                    loadingPromise.value = null

                    resolve(permissions.value)
                })
                .catch(error => {
                    console.error('Error loading permissions:', error)
                    loadingPromise.value = null
                    reject(error)
                })
        })

        loadingPromise.value = promise

        return promise
    }

    const clearPermissionsCache = () => {
        permissions.value = []
        companyId.value = null
        isLoaded.value = false
        sessionStorage.removeItem('user_permissions')
        sessionStorage.removeItem('current_company_id')
    }

    const featureFlagValuePromise = async (featureFlagName, targetCompanyId = null) => {
        if (targetCompanyId === null) {
            targetCompanyId = companyId.value
        }

        try {
            const url = buildCompanyApiInternalRoute(`options/${featureFlagName}`, targetCompanyId)
            const response = await axios.get(url, { validateStatus: false })

            if (response.status !== 200) {
                return null
            }

            if (response.data.data.hasOwnProperty('value')) {
                return response.data.data['value']
            }

            return null
        } catch (error) {
            console.error('Error loading feature flag:', error)
            return null
        }
    }

    const urlCompanyId = computed(() => getCompanyId('sonicu'))

    const isSonicu = computed(() => can('do_anything'))

    const isSonicuViewOnly = computed(() => can('see_anything'))

    const isPartner = computed(() => can('is_partner'))

    const isLocalEnvironment = computed(() => {
        const host = window.location.host
        return host === 'localhost' || host === '127.0.0.1'
    })

    const isStagingEnvironment = computed(() => {
        const host = window.location.host
        return host === 'sonistaging.com' || host === 'www.sonistaging.com'
    })

    const isQaEnvironment = computed(() => {
        const host = window.location.host
        return host === 'qa.sonicumonitoring.com'
    })

    const isProductionEnvironment = computed(() => {
        const host = window.location.host
        return host === 'www.sonicumonitoring.com' || host === 'sonicumonitoring.com'
    })

    return {
        // State
        companyId,
        permissions,
        localCompanyId,

        // Methods
        can,
        getPermissions,
        canAsync,
        loadCompany,
        isCompanyLoaded,
        clearPermissionsCache,
        featureFlagValuePromise,

        // Computed
        urlCompanyId,
        isSonicu,
        isSonicuViewOnly,
        isPartner,
        isLocalEnvironment,
        isStagingEnvironment,
        isQaEnvironment,
        isProductionEnvironment,
    }
}